import {tx, ptx, usePageText} from '../lib/page-text';
import { ImageUpload } from './ImageUpload';
import { useState } from 'react';
import { useNavigate } from '../lib/localized-router';
import type { LiveSolution } from '../../../api/src/solutions/solution-catalog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Modal } from './ui';

/** Create from an explicitly selected solution or saved template. */
export function CreateStreamFromPreset({ title, blueprint, solution, onClose }: { solution?: LiveSolution; title: string; blueprint?: { id: string; name: string }; onClose: () => void }) {
  usePageText();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [industry, setIndustry] = useState(solution?.industries[0] ?? '');
  const today = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const [name, setName] = useState(`${title} · ${today}`);
  const create = useMutation({
    mutationFn: async () => (await api.post('/me/streams', {
      name: name.trim(),
      ...(coverImageUrl ? { coverImageUrl } : {}),
      ...(solution ? { solutionId: solution.id, industry } : {}),
      ...(blueprint ? { blueprintId: blueprint.id } : {}),
    })).data as { id: string },
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ['my-streams'] }); navigate(`/streams/${s.id}?tab=prepare`); },
  });
  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p className="modal-subtitle">
        {blueprint ? <>{tx("Set up with the ")}<strong>{blueprint.name}</strong>{tx(" settings. You can change them afterwards.")}</> : tx("Give it a name — you can change it later.")}
      </p>
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim().length >= 2 && !create.isPending && !uploading && (!solution || blueprint)) create.mutate(); }}>
        <label className="label" htmlFor="solution-event-name">{tx("Event name")}</label>
        <input id="solution-event-name" className="input" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        <ImageUpload label={tx("Event cover image")} value={coverImageUrl} onChange={setCoverImageUrl} disabled={create.isPending} onBusy={setUploading} />
        {solution && <>
          <label className="label" htmlFor="solution-industry">{tx("Industry")}</label>
          <select id="solution-industry" className="input" value={industry} onChange={e => setIndustry(e.target.value)}>{solution.industries.map(i => <option key={i}>{i}</option>)}</select>
          <p>{solution.description}</p>
          <p className="muted"><strong>{tx("Planned, not included:")}</strong> {solution.planned.join('; ')}.</p>
        </>}
        {create.isError && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
            {(create.error as any)?.response?.data?.message ?? tx("Could not create the stream.")}
          </div>
        )}
        <div className="flex gap" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
          <button className="btn primary" disabled={name.trim().length < 2 || create.isPending || uploading || (!!solution && !blueprint)}>
            {create.isPending ? tx("Creating…") : tx("Create and open")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
